import { useEffect, useMemo, useState, useCallback } from 'react';
import { convertCurrencyToSign, DATE_FORMAT, DEFAULT_CURRENCY } from '@received/pricing-model';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Contract } from '../../../../types/contractTypes';
import { Customer } from '../../../../types/customerTypes';
import { getValueWithCurrencySign, numberWithCommas } from '../../../../utils/NumberUtils';
import { getSuppliers } from '../../../../utils/CustomerUtils';
import { images } from '../../../../constants/images';
import { Button, Icon, PDFPreviewModal } from '../../../../components';
import {
	getLineItems,
	POLineItem,
	getOrderTotals,
	getPaymentTermsLabel,
	formatPriceWithBillingCycle,
	getPaymentTermsInfo,
	buildPurchaseOrderData,
} from './PurchaseOrder.utils';
import { billingCycleLabelsByEnum } from '../../../../constants/generalConstants';
import { downloadPurchaseOrderPDF } from './PurchaseOrder.download';
import styles from './PurchaseOrder.module.scss';

interface PurchaseOrderProps {
	contract: Contract;
	isValid?: boolean;
	isPartner: boolean;
}

export const PurchaseOrder = ({ contract, isValid }: PurchaseOrderProps) => {
	const { t } = useTranslation('translation');
	const dispatch = useDispatch();
	const [suppliers, setSuppliers] = useState<Customer[]>([]);
	const [isPreviewOpen, setIsPreviewOpen] = useState(false);

	const currency = contract?.pricingModel?.tabs?.[0]?.currency || DEFAULT_CURRENCY;
	const currencySign = convertCurrencyToSign(currency);

	const customer = contract?.customer;
	// Find the supplier (Debit Entity) by looking up supplierId in suppliers list
	const supplierId = contract?.pricingModel?.tabs?.find((tab) => tab.supplierId)?.supplierId;
	const supplier = suppliers.find((s) => s.id === supplierId);

	const lineItems = useMemo(() => getLineItems(contract?.pricingModel?.tabs), [contract?.pricingModel?.tabs]);
	const totals = useMemo(() => getOrderTotals(contract?.pricingModel?.tabs), [contract?.pricingModel?.tabs]);
	const paymentTermsInfo = useMemo(() => getPaymentTermsInfo(contract?.pricingModel?.tabs), [contract?.pricingModel?.tabs]);

	// Create purchase order data for PDF preview
	const purchaseOrderData = useMemo(
		() =>
			buildPurchaseOrderData({
				contract,
				customer,
				supplier,
				currencySign,
				lineItems,
				totals,
				paymentTermsInfo,
			}),
		[contract, customer, supplier, currencySign, lineItems, totals, paymentTermsInfo],
	);

	useEffect(() => {
		// Only fetch suppliers if we have a supplierId to look up and don't have data yet
		if (supplierId && suppliers.length === 0) {
			const fetchSuppliers = async () => {
				const data = await getSuppliers(dispatch);
				if (data) setSuppliers(data);
			};
			fetchSuppliers();
		}
	}, [dispatch, supplierId, suppliers.length]);

	const handleDownload = useCallback(async () => {
		try {
			await downloadPurchaseOrderPDF(purchaseOrderData);
		} catch (error) {
			console.error('Error downloading PDF:', error);
		}
	}, [purchaseOrderData]);

	return (
		<div className={styles.container}>
			{/* EMPTY STATE */}
			{!isValid ? (
				<div className={clsx(styles.fullBox, styles.emptyState)}>
					<div className={styles.emptyStateBody}>
						<img alt='purchase-order-empty-state' src={images.papers} className={styles.noDataImage} />
						<div className={styles.flexColumnCenter}>
							<span className={styles.noDataTitle}>{t('completeFields')}</span>
							<span className={styles.noDataDesc}>{t('addInformation')}</span>
						</div>
					</div>
				</div>
			) : (
				<div className={styles.poDocument}>
					{/* HEADER */}
					<div className={styles.header}>
						<div className={styles.headerLeft}>
							<h1 className={styles.poTitle}>{t('purchaseOrder')}</h1>
							<div className={styles.poNumber}>
								<span className={styles.label}>{t('poNumber')}:</span>
								<span className={styles.value}>{contract?.poNumber || `#${contract?.contractNumber}`}</span>
							</div>
							<div className={styles.issueDate}>
								<span className={styles.label}>{t('issueDate')}:</span>
								<span className={styles.value}>{dayjs(contract?.createdAt).format(DATE_FORMAT)}</span>
							</div>
						</div>
						<div className={styles.headerActions}>
							<Button type='outlined' color='neutral' onClick={() => setIsPreviewOpen(true)}>
								<Icon imgType='eye' color='neutral700' />
								{t('preview')}
							</Button>
							<Button color='success'>
								<Icon imgType='paper_airplane' color='white' />
								{t('sendToSign')}
							</Button>
						</div>
					</div>

					{/* PARTIES */}
					<div className={styles.parties}>
						<div className={styles.partyBox}>
							<h3 className={styles.partyTitle}>{t('buyer')}</h3>
							<div className={styles.partyDetails}>
								<span className={styles.partyName}>{customer?.name || '-'}</span>
								{customer?.legalName && <span className={styles.partyLegalName}>{customer.legalName}</span>}
								{customer?.billingAddress && <span className={styles.partyAddress}>{customer.billingAddress}</span>}
								{customer?.ein && (
									<span className={styles.partyTaxId}>
										{t('taxId')}: {customer.ein}
									</span>
								)}
							</div>
						</div>

						<div className={styles.partyBox}>
							<h3 className={styles.partyTitle}>{t('seller')}</h3>
							<div className={styles.partyDetails}>
								<span className={styles.partyName}>{supplier?.name || '-'}</span>
								{supplier?.legalName && <span className={styles.partyLegalName}>{supplier.legalName}</span>}
								{supplier?.billingAddress && <span className={styles.partyAddress}>{supplier.billingAddress}</span>}
								{supplier?.ein && (
									<span className={styles.partyTaxId}>
										{t('taxId')}: {supplier.ein}
									</span>
								)}
							</div>
						</div>
					</div>

					{/* CONTRACT PERIOD */}
					<div className={styles.periodSection}>
						<h3 className={styles.sectionTitle}>{t('contractPeriod')}</h3>
						<div className={styles.periodDates}>
							<span className={styles.periodDate}>
								{dayjs(contract?.activationStartDate).format(DATE_FORMAT)}
								{contract?.activationEndDate && ` - ${dayjs(contract?.activationEndDate).format(DATE_FORMAT)}`}
							</span>
						</div>
					</div>

					{/* LINE ITEMS TABLE */}
					<div className={styles.itemsSection}>
						<h3 className={styles.sectionTitle}>{t('orderItems')}</h3>
						<table className={styles.itemsTable}>
							<thead>
								<tr>
									<th className={styles.itemCol}>{t('products')}</th>
									<th className={styles.periodCol}>{t('period')}</th>
									<th className={styles.durationCol}>{t('duration')}</th>
									<th className={styles.qtyCol}>{t('qty')}</th>
									<th className={styles.priceCol}>{t('unitPrice')}</th>
									<th className={styles.totalCol}>{t('Total')}</th>
								</tr>
							</thead>
							<tbody>
								{lineItems.map((item: POLineItem, index: number) => (
									<tr key={index}>
										<td className={styles.itemCol}>
											<span className={styles.itemName}>{item.name}</span>
											{item.description && <span className={styles.itemDescription}>{item.description}</span>}
										</td>
										<td className={styles.periodCol}>
											{item.billingStartDate && (
												<span>
													{dayjs(item.billingStartDate).format(DATE_FORMAT)}
													{item.billingEndDate && ` - ${dayjs(item.billingEndDate).format(DATE_FORMAT)}`}
												</span>
											)}
										</td>
										<td className={styles.durationCol}>{item.duration}</td>
										<td className={styles.qtyCol}>{numberWithCommas(item.quantity)}</td>
										<td className={styles.priceCol}>{formatPriceWithBillingCycle(item.unitPrice, currencySign, item.billingCycleUnit)}</td>
										<td className={styles.totalCol}>{formatPriceWithBillingCycle(item.total, currencySign, item.billingCycleUnit)}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					{/* TOTALS */}
					<div className={styles.totalsSection}>
						<div className={styles.totalsBox}>
							<div className={styles.totalRow}>
								<span className={styles.totalLabel}>{t('Subtotal')}</span>
								<span className={styles.totalValue}>{getValueWithCurrencySign(totals.subTotal, currencySign)}</span>
							</div>
							{totals.discount > 0 && (
								<div className={styles.totalRow}>
									<span className={styles.totalLabel}>{t('discount')}</span>
									<span className={styles.totalValue}>({getValueWithCurrencySign(totals.discount, currencySign)})</span>
								</div>
							)}
							{totals.tax > 0 && (
								<div className={styles.totalRow}>
									<span className={styles.totalLabel}>{t('tax')}</span>
									<span className={styles.totalValue}>{getValueWithCurrencySign(totals.tax, currencySign)}</span>
								</div>
							)}
							<div className={clsx(styles.totalRow, styles.grandTotal)}>
								<span className={styles.totalLabel}>{t('Total')}</span>
								<span className={styles.totalValue}>{getValueWithCurrencySign(totals.total, currencySign)}</span>
							</div>
						</div>
					</div>

					{/* PAYMENT TERMS */}
					<div className={styles.termsSection}>
						<h3 className={styles.sectionTitle}>{t('paymentTerms')}</h3>
						{paymentTermsInfo.isIdentical ? (
							<div className={styles.termsContent}>
								{paymentTermsInfo.netTerms !== undefined && (
									<div className={styles.termRow}>
										<span className={styles.termLabel}>{t('netTerms')}:</span>
										<span className={styles.termValue}>{getPaymentTermsLabel(paymentTermsInfo.netTerms)}</span>
									</div>
								)}
								{paymentTermsInfo.billingCycleUnit && (
									<div className={styles.termRow}>
										<span className={styles.termLabel}>{t('billingCycle')}:</span>
										<span className={styles.termValue}>{billingCycleLabelsByEnum[paymentTermsInfo.billingCycleUnit]}</span>
									</div>
								)}
							</div>
						) : (
							<div className={styles.termsPerProduct}>
								{paymentTermsInfo.productTerms.map((product, index) => (
									<div key={index} className={styles.productTerms}>
										<span className={styles.productTermsName}>{product.productName}</span>
										<div className={styles.termsContent}>
											{product.netTerms !== undefined && (
												<div className={styles.termRow}>
													<span className={styles.termLabel}>{t('netTerms')}:</span>
													<span className={styles.termValue}>{getPaymentTermsLabel(product.netTerms)}</span>
												</div>
											)}
											{product.billingCycleUnit && (
												<div className={styles.termRow}>
													<span className={styles.termLabel}>{t('billingCycle')}:</span>
													<span className={styles.termValue}>{billingCycleLabelsByEnum[product.billingCycleUnit]}</span>
												</div>
											)}
										</div>
									</div>
								))}
							</div>
						)}
					</div>
				</div>
			)}

			{/* PDF Preview Modal */}
			<PDFPreviewModal
				isOpen={isPreviewOpen}
				documentsList={[purchaseOrderData]}
				onClose={() => setIsPreviewOpen(false)}
				onDownload={handleDownload}
			/>
		</div>
	);
};
